/**
 * F-4.6 - Forensic Timeline page.
 *
 * Cross-product event timeline (Activity events + DDR decisions +
 * Discovery findings + Compliance breaches), interleaved + filterable.
 *
 * Routes:
 *   GET /v1/activity/timeline/buckets - histogram
 *   GET /v1/activity/timeline         - event list
 *   GET /v1/activity/timeline/export  - JSON bundle with sha256 manifest
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Clock, Download, Filter, AlertTriangle, ShieldAlert, Database,
  Activity as ActivityIcon, GitBranch, Loader2, ExternalLink,
} from 'lucide-react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Brush,
} from 'recharts';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { API_BASE, apiFetch, getAuthToken } from '@/lib/api';

type TimelineSeverity = 'critical' | 'high' | 'medium' | 'info';
type TimelineProduct = 'activity' | 'ddr' | 'discovery' | 'compliance';

interface TimelineBucket {
  ts: string;
  total: number;
  bySeverity: Record<TimelineSeverity, number>;
  byDecision: Record<string, number>;
  byProduct: Record<TimelineProduct, number>;
}

interface TimelineEvent {
  id: string;
  occurredAt: string;
  product: TimelineProduct;
  type: string;
  severity: TimelineSeverity;
  title: string;
  detail: string;
  actorUserId: string | null;
  assetId: string | null;
  requestId: string | null;
  payload: Record<string, unknown>;
}

type RangePreset = '1h' | '24h' | '7d' | '30d';

function presetWindow(preset: RangePreset): { since: Date; until: Date; bucket: '5m' | '1h' | '1d' } {
  const until = new Date();
  const since = new Date(until);
  let bucket: '5m' | '1h' | '1d' = '1h';
  if (preset === '1h')  { since.setHours(since.getHours() - 1);  bucket = '5m'; }
  if (preset === '24h') { since.setDate(since.getDate() - 1);    bucket = '1h'; }
  if (preset === '7d')  { since.setDate(since.getDate() - 7);    bucket = '1h'; }
  if (preset === '30d') { since.setDate(since.getDate() - 30);   bucket = '1d'; }
  return { since, until, bucket };
}

const PRODUCT_ICON: Record<TimelineProduct, React.ComponentType<{ className?: string }>> = {
  activity:   ActivityIcon,
  ddr:        ShieldAlert,
  discovery:  Database,
  compliance: AlertTriangle,
};

const SEVERITY_BADGE: Record<TimelineSeverity, 'critical' | 'high' | 'warning' | 'info'> = {
  critical: 'critical',
  high:     'high',
  medium:   'warning',
  info:     'info',
};

export function ActivityTimeline() {
  const [range, setRange] = useState<RangePreset>('24h');
  const [productFilter, setProductFilter] = useState<TimelineProduct | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<TimelineSeverity | 'all'>('all');
  const [buckets, setBuckets] = useState<TimelineBucket[] | null>(null);
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const win = useMemo(() => presetWindow(range), [range]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      since: win.since.toISOString(),
      until: win.until.toISOString(),
      bucket: win.bucket,
      limit: '200',
    });
    if (severityFilter !== 'all') qs.set('severity', severityFilter);
    Promise.all([
      apiFetch<{ buckets: TimelineBucket[] }>(`/v1/activity/timeline/buckets?${qs}`, { signal: ctrl.signal }),
      apiFetch<{ events: TimelineEvent[] }>(`/v1/activity/timeline?${qs}`, { signal: ctrl.signal }),
    ])
      .then(([b, e]) => {
        if (!ctrl.signal.aborted) {
          setBuckets(b.buckets);
          setEvents(e.events);
        }
      })
      .catch((err) => !ctrl.signal.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !ctrl.signal.aborted && setLoading(false));
    return () => ctrl.abort();
  }, [win, severityFilter]);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return events.filter((e) => productFilter === 'all' || e.product === productFilter);
  }, [events, productFilter]);

  async function onExport() {
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        since: win.since.toISOString(),
        until: win.until.toISOString(),
        limit: '5000',
      });
      if (severityFilter !== 'all') qs.set('severity', severityFilter);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/v1/activity/timeline/export?${qs}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`export ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m?.[1] ?? `foctta-timeline-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      globalThis.alert(`Export failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setExporting(false);
    }
  }

  if (loading && !buckets) {
    return (
      <PageContainer>
        <PageHeader title="Forensic Timeline" />
        <LoadingState caption="Loading timeline..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Forensic Timeline" />
        <ErrorState title="Failed to load timeline" detail={error} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Forensic Timeline" />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1">
          {(['1h', '24h', '7d', '30d'] as const).map((r) => (
            <Button
              key={r}
              size="sm"
              variant={range === r ? 'default' : 'glass'}
              onClick={() => setRange(r)}
            >
              {r === '1h' ? 'Last 1h' : r === '24h' ? 'Last 24h' : r === '7d' ? 'Last 7d' : 'Last 30d'}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 ml-2">
          {(['all', 'activity', 'ddr', 'discovery', 'compliance'] as const).map((p) => (
            <Button
              key={p}
              size="sm"
              variant={productFilter === p ? 'default' : 'glass'}
              onClick={() => setProductFilter(p)}
              className="capitalize"
            >
              {p}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 ml-2">
          {(['all', 'critical', 'high', 'medium', 'info'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={severityFilter === s ? 'default' : 'glass'}
              onClick={() => setSeverityFilter(s)}
              className="capitalize"
            >
              {s}
            </Button>
          ))}
        </div>
        <div className="ml-auto">
          <Button size="sm" variant="glass" onClick={onExport} disabled={exporting}>
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export forensic JSON
          </Button>
        </div>
      </div>

      {/* Histogram */}
      <GlassCard className="mb-4">
        <CardHeader>
          <CardTitle className="text-sm">Event volume</CardTitle>
          <CardDescription className="text-xs">
            {buckets?.length ?? 0} buckets ({win.bucket}) over {range} - drag the brush to zoom.
          </CardDescription>
        </CardHeader>
        <div className="px-4 pb-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={buckets ?? []}>
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 10 }}
                tickFormatter={(v: string) => new Date(v).toLocaleTimeString('en-IN', { hour12: false })}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                labelFormatter={(v) => new Date(String(v)).toLocaleString('en-IN')}
                contentStyle={{ background: 'rgba(0,0,0,0.85)', border: 'none', fontSize: 11 }}
              />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.critical} stackId="sev" fill="#ef4444" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.high}     stackId="sev" fill="#f97316" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.medium}   stackId="sev" fill="#f59e0b" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.info}     stackId="sev" fill="#60a5fa" />
              <Brush dataKey="ts" height={20} stroke="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Event list */}
      <GlassCard>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" />
            Events ({filteredEvents.length})
          </CardTitle>
        </CardHeader>
        <div className="divide-y divide-border/20">
          {filteredEvents.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground">
              No events in the selected win.
            </div>
          ) : (
            filteredEvents.map((e) => {
              const Icon = PRODUCT_ICON[e.product] ?? GitBranch;
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} className="px-3 py-2.5">
                  <button
                    type="button"
                    className="w-full text-left flex items-start gap-2 hover:bg-muted/30 -mx-1 px-1 py-0.5 rounded"
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={SEVERITY_BADGE[e.severity]} className="text-[9px] capitalize">
                          {e.severity}
                        </Badge>
                        <Badge variant="glass" className="text-[9px] capitalize">{e.product}</Badge>
                        <code className="text-[10px] font-mono text-muted-foreground">{e.type}</code>
                        <span className="ml-auto text-[10px] tabular text-muted-foreground">
                          {new Date(e.occurredAt).toLocaleTimeString('en-IN', { hour12: false })}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-foreground/90 truncate">{e.title}</div>
                      {!isOpen && e.detail && (
                        <div className="text-[11px] text-muted-foreground truncate">{e.detail}</div>
                      )}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="mt-2 ml-6 space-y-2">
                      <div className="text-[11px] text-foreground/90 whitespace-pre-wrap">{e.detail}</div>
                      {e.actorUserId && (
                        <div className="text-[10px] text-muted-foreground">Actor: <code className="font-mono">{e.actorUserId}</code></div>
                      )}
                      {e.assetId && (
                        <div className="text-[10px] text-muted-foreground">Asset: <code className="font-mono">{e.assetId}</code></div>
                      )}
                      {e.requestId && (
                        <Button
                          size="sm"
                          variant="glass"
                          onClick={() => {
                            globalThis.location.href = `/detection/decisions?reqId=${encodeURIComponent(e.requestId!)}`;
                          }}
                        >
                          Why blocked? <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                      <details>
                        <summary className="text-[10px] text-muted-foreground cursor-pointer">raw payload</summary>
                        <pre className="text-[10px] font-mono mt-1 bg-muted/30 p-2 rounded overflow-x-auto max-h-48">
                          {JSON.stringify(e.payload, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}
